import React, { memo, useMemo, useCallback, DragEvent } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { GalleryBlockSettings } from './GalleryBlockSettings';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Container } from 'reactstrap';
import { FormText } from '@core/components/form/FormText';
import { useDidMount } from '@core/utils/hooks/react';
import { DropableImage } from '@modules/common/DropableImage';
import { CourseService } from '@business/services';

export const GALLERY_BLOCK_AI_INSTRUCTION = `
GALLERY BLOCK - AI Instruction Format for Widget Variations

The "gallery" block presents multiple images in different layouts. It supports grid and carousel-based formats and optional captions.

Supported Variations:
1. "carousel" — Images displayed in a single rotating view
2. "twoColumnGrid" — Images shown in a 2-column grid
3. "threeColumnGrid" — Images shown in a 3-column grid
4. "fourColumnGrid" — Images shown in a 4-column grid

Required JSON Structure:
- type: "gallery"
- variation: one of the supported types above
- data: {
    items: Array of {
      image: string (URL),
      text: string (caption),
      menuOrder: number (image order)
    },
    captions: boolean,            // true if captions should be shown
  }

Example:
{
  "type": "gallery",
  "variation": "threeColumnGrid",
  "data": {
    "items": [
      {
        "image": "https://example.com/image1.jpg",
        "text": "Caption 1",
        "menuOrder": 0
      },
      {
        "image": "https://example.com/image2.jpg",
        "text": "Caption 2",
        "menuOrder": 1
      },
    ],
    "captions": true,
  }
}
`;

function defineGalleryBlockItemForm() {
  return formGroup({
    image: formControl<string>({
      initialValue: 'https://startuppakistan.com.pk/wp-content/uploads/2022/02/best-bicycle-696x392.jpg',
    }),
    text: formControl<string>({
      initialValue: 'Add Caption',
    }),
    menuOrder: formControl<number>({
      initialValue: 0,
    }),
  });
}

function defineGalleryBlockForm() {
  return formGroup({
    items: formArray(() => defineGalleryBlockItemForm()),
    captions: formControl<boolean>({
      initialValue: true,
      label: 'Image Captions',
    }),
    advance: defineBlockAdvanceForm(),
  });
}

export type GalleryWidgetFormData = FormDataType<typeof defineGalleryBlockForm>;
export type GalleryWidgetForm = ReturnType<typeof defineGalleryBlockForm>;

export enum GalleryBlockVariation {
  carousel = 'carousel',
  twoColumnGrid = 'twoColumnGrid',
  threeColumnGrid = 'threeColumnGrid',
  fourColumnGrid = 'fourColumnGrid',
}

const columnMap: { [key in GalleryBlockVariation]: number } = {
  [GalleryBlockVariation.carousel]: 1,
  [GalleryBlockVariation.twoColumnGrid]: 2,
  [GalleryBlockVariation.threeColumnGrid]: 3,
  [GalleryBlockVariation.fourColumnGrid]: 4,
};

export const GalleryBlock = memo((props: CourseBuilder.BlockProps<GalleryBlockVariation>) => {
  const { variation, preview, editable } = props;
  const columns = columnMap[variation];
  const form = useMemo(defineGalleryBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const itemValues = useObservable(form.controls.items.items$, []);

  useDidMount(() => {
    for (let i = 0; i < columns; i++) {
      form.controls.items.push().patchValue({ menuOrder: i, image: CourseService.getGalleryDummyImages()[i] });
    }
  });
  useConfigureBlock({
    props,
    form,
    contentComponent: GalleryBlockSettings,
    contentProps: { form },
  });

  const onDropImage = useCallback(
    (file: File) => {
      UploadAPI.upload('courses/images', file)
        .pipe(tap(res => form.controls.items.push().patchValue({ image: res.fileUrl })))
        .subscribe();
    },
    [form],
  );

  return (
    <BlockContainer className={classNames('block-image', variation, preview)} advanceForm={formValue.advance}>
      <Container>
        <div style={{ display: 'grid ', gridTemplateColumns: Array(columns + 1).join('1fr '), columnGap: '1em', rowGap: '2em' }}>
          {itemValues.map((ctrl, i) => (
            <div className="image-centered" key={i}>
              <DropableImage key={i} onDropImage={onDropImage} src={ctrl.value.image} />
              {formValue.captions && <FormText control={ctrl.controls.text} editable={editable} glossify={!editable} />}
            </div>
          ))}
        </div>
      </Container>
    </BlockContainer>
  );
});
